<template>
    <div class="lottery-draw">
        <Toast />

        <div class="lottery-draw__filters">
            <div class="lottery-draw__row">
                <div class="lottery-draw__field">
                    <label for="lottery-year" class="lottery-draw__label">År</label>
                    <Select
                        id="lottery-year"
                        v-model="filters.year"
                        :options="yearOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle år"
                        showClear
                        style="width: 100%"
                        @change="onYearChange"
                    />
                </div>

                <div class="lottery-draw__field">
                    <label for="lottery-date-toggle" class="lottery-draw__label">Aktivitetsdato</label>
                    <div class="lottery-draw__date-range">
                        <Checkbox v-model="filters.dateRangeEnabled" inputId="lottery-date-toggle" binary />
                        <DatePicker
                            v-model="filters.dateFrom"
                            :disabled="!filters.dateRangeEnabled"
                            :minDate="yearMinDate"
                            :maxDate="yearMaxDate"
                            placeholder="Fra"
                            dateFormat="dd.mm.yy"
                            showIcon
                            style="width: 100%"
                        />
                        <DatePicker
                            v-model="filters.dateTo"
                            :disabled="!filters.dateRangeEnabled"
                            :minDate="yearMinDate"
                            :maxDate="yearMaxDate"
                            placeholder="Til"
                            dateFormat="dd.mm.yy"
                            showIcon
                            style="width: 100%"
                        />
                    </div>
                </div>
            </div>

            <div class="lottery-draw__row">
                <div class="lottery-draw__field">
                    <label for="lottery-itemgroup" class="lottery-draw__label">Plukkegruppe</label>
                    <Select
                        id="lottery-itemgroup"
                        v-model="filters.itemGroupId"
                        :options="itemGroupOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle grupper"
                        showClear
                        style="width: 100%"
                        @change="onItemGroupChange"
                    />
                </div>

                <div class="lottery-draw__field">
                    <label for="lottery-item" class="lottery-draw__label">Type</label>
                    <Select
                        id="lottery-item"
                        v-model="filters.itemId"
                        :options="itemOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Alle typer"
                        showClear
                        style="width: 100%"
                    >
                        <template #value>
                            <span v-if="selectedItem" class="lottery-draw__item-option">
                                <img
                                    v-if="selectedItem.illustration_src"
                                    :src="selectedItem.illustration_src"
                                    :alt="selectedItem.illustration_alt || selectedItem.label"
                                    class="lottery-draw__item-icon"
                                >
                                {{ selectedItem.label }}
                            </span>
                            <span v-else>Alle typer</span>
                        </template>
                        <template #option="{ option }">
                            <span class="lottery-draw__item-option">
                                <img
                                    v-if="option.illustration_src"
                                    :src="option.illustration_src"
                                    :alt="option.illustration_alt || option.label"
                                    class="lottery-draw__item-icon"
                                >
                                {{ option.label }}
                            </span>
                        </template>
                    </Select>
                </div>
            </div>

            <div class="lottery-draw__row">
                <div class="lottery-draw__field lottery-draw__field--narrow">
                    <label for="lottery-min-quantity" class="lottery-draw__label">Minimum mengde</label>
                    <Select
                        id="lottery-min-quantity"
                        v-model="filters.minQuantity"
                        :options="minQuantityOptions"
                        optionLabel="label"
                        optionValue="value"
                        style="width: 100%"
                    />
                </div>
            </div>
        </div>

        <div class="lottery-draw__actions">
            <Button label="Trekk vinner" icon="pi pi-ticket" :loading="drawing" @click="draw" />
        </div>

        <div v-if="hasDrawn" class="lottery-draw__result">
            <template v-if="winner">
                <h5 class="lottery-draw__result-title">Vinner</h5>
                <dl class="lottery-draw__personalia">
                    <dt>Navn</dt>
                    <dd>
                        {{ winner.name }}
                        <i v-if="winner.is_anonymous" class="pi pi-eye-slash" aria-label="Anonym"></i>
                    </dd>

                    <dt>E-post</dt>
                    <dd>{{ winner.email }}</dd>

                    <dt>Mobil</dt>
                    <dd>{{ winner.mobile || '–' }}</dd>

                    <dt>Kommune</dt>
                    <dd>{{ winner.location_name || '–' }}</dd>

                    <dt>Brukergrupper</dt>
                    <dd class="lottery-draw__groups">
                        <span v-if="winner.groups.length === 0">Ingen</span>
                        <Chip v-for="group of winner.groups" :key="group.id" :label="group.name" />
                    </dd>

                    <dt>Samlet mengde</dt>
                    <dd>{{ winner.total_kg }} kg</dd>
                </dl>
                <p class="lottery-draw__pool-size">
                    Trukket blant {{ poolSize }} {{ poolSize === 1 ? 'kvalifisert bruker' : 'kvalifiserte brukere' }}.
                </p>
            </template>
            <template v-else>
                <p class="lottery-draw__empty">Ingen brukere oppfyller de valgte kriteriene.</p>
            </template>
        </div>
    </div>
</template>
<script>
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import Button from 'primevue/button';
import Chip from 'primevue/chip';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';

function toIsoDate(date) {
    if (!date) {
        return null;
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export default {
    components: {
        Select,
        Checkbox,
        DatePicker,
        Button,
        Chip,
        Toast,
    },
    setup() {
        return {
            toast: useToast(),
        };
    },
    data() {
        return {
            itemGroups: [],
            years: [],
            filters: {
                year: null,
                itemGroupId: null,
                itemId: null,
                minQuantity: 0,
                dateRangeEnabled: false,
                dateFrom: null,
                dateTo: null,
            },
            minQuantityOptions: [
                { label: 'Ingen', value: 0 },
                { label: '1 kg', value: 1 },
                { label: '2 kg', value: 2 },
                { label: '5 kg', value: 5 },
                { label: '10 kg', value: 10 },
                { label: '20 kg', value: 20 },
            ],
            drawing: false,
            hasDrawn: false,
            winner: null,
            poolSize: 0,
        };
    },
    computed: {
        yearOptions() {
            return this.years.map(year => ({ label: String(year), value: year })).sort((a, b) => b.value - a.value);
        },
        itemGroupOptions() {
            return this.itemGroups.map(group => ({ label: group.name, value: group.id }));
        },
        itemOptions() {
            const groups = this.filters.itemGroupId
                ? this.itemGroups.filter(group => group.id === this.filters.itemGroupId)
                : this.itemGroups;

            return groups
                .flatMap(group => group.items)
                .map(item => ({
                    label: item.name,
                    value: item.id,
                    illustration_src: item.illustration_src,
                    illustration_alt: item.illustration_alt,
                }));
        },
        selectedItem() {
            return this.itemOptions.find(option => option.value === this.filters.itemId) || null;
        },
        yearMinDate() {
            return this.filters.year ? new Date(this.filters.year, 0, 1) : null;
        },
        yearMaxDate() {
            return this.filters.year ? new Date(this.filters.year, 11, 31) : null;
        },
    },
    mounted() {
        this.fetchFilters();
    },
    methods: {
        onItemGroupChange() {
            if (!this.itemOptions.some(option => option.value === this.filters.itemId)) {
                this.filters.itemId = null;
            }
        },
        onYearChange() {
            if (!this.filters.year) {
                return;
            }

            if (this.filters.dateFrom && (this.filters.dateFrom < this.yearMinDate || this.filters.dateFrom > this.yearMaxDate)) {
                this.filters.dateFrom = null;
            }
            if (this.filters.dateTo && (this.filters.dateTo < this.yearMinDate || this.filters.dateTo > this.yearMaxDate)) {
                this.filters.dateTo = null;
            }
        },
        async fetchFilters() {
            try {
                const response = await fetch('/api/v1/lottery/filters');
                const data = await response.json();
                this.itemGroups = data.itemGroups;
                this.years = data.years;
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke hente filtre.', life: 3000 });
            }
        },
        async draw() {
            this.drawing = true;

            try {
                const params = new URLSearchParams();

                if (this.filters.year) {
                    params.set('year', this.filters.year);
                }
                if (this.filters.itemGroupId) {
                    params.set('item_group_id', this.filters.itemGroupId);
                }
                if (this.filters.itemId) {
                    params.set('item_id', this.filters.itemId);
                }
                if (this.filters.minQuantity) {
                    params.set('min_quantity', this.filters.minQuantity);
                }
                if (this.filters.dateRangeEnabled) {
                    const dateFrom = toIsoDate(this.filters.dateFrom);
                    const dateTo = toIsoDate(this.filters.dateTo);

                    if (dateFrom) {
                        params.set('date_from', dateFrom);
                    }
                    if (dateTo) {
                        params.set('date_to', dateTo);
                    }
                }

                const response = await fetch(`/api/v1/lottery/draw?${params.toString()}`, { cache: 'no-store' });

                if (!response.ok) {
                    throw new Error('request failed');
                }

                const data = await response.json();
                this.winner = data.winner;
                this.poolSize = data.pool_size;
                this.hasDrawn = true;
            } catch (error) {
                console.error(error);
                this.toast.add({ severity: 'error', summary: 'Feil', detail: 'Kunne ikke trekke en vinner.', life: 3000 });
            } finally {
                this.drawing = false;
            }
        },
    },
};
</script>
<style scoped>
.lottery-draw__filters {
    margin-bottom: 1.5rem;
}

.lottery-draw__row {
    display: flex;
    flex-wrap: wrap;
    gap: 1.25rem;
    margin-bottom: 1.25rem;
}

.lottery-draw__field {
    flex: 1 1 200px;
}

.lottery-draw__field--narrow {
    flex: 0 1 240px;
}

.lottery-draw__label {
    display: block;
    margin-bottom: 0.5rem;
}

.lottery-draw__item-option {
    display: flex;
    align-items: center;
    gap: 8px;
}

.lottery-draw__item-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
}

.lottery-draw__date-range {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.lottery-draw__actions {
    margin-bottom: 1.5rem;
}

.lottery-draw__result {
    border-top: 1px solid var(--p-content-border-color, #dee2e6);
    padding-top: 1.5rem;
}

.lottery-draw__result-title {
    margin-top: 0;
}

.lottery-draw__personalia {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.5rem 1rem;
    margin: 0 0 1rem;
}

.lottery-draw__personalia dt {
    font-weight: 600;
    color: var(--p-text-muted-color, #6c757d);
}

.lottery-draw__personalia dd {
    margin: 0;
}

.lottery-draw__groups {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.lottery-draw__pool-size {
    color: var(--p-text-muted-color, #6c757d);
    margin: 0;
}

.lottery-draw__empty {
    margin: 0;
}
</style>
